import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/questions";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PriceButton } from "@/components/AgentPrices";
import { productForm, productApplication } from "@/lib/productInfo";

export default function Results() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [category, setCategory] = useState("");
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [requestProduct, setRequestProduct] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null); // {url, name}

  useEffect(() => {
    const answers = JSON.parse(sessionStorage.getItem("tm_answers") || "{}");
    const cat = sessionStorage.getItem("tm_category");
    if (!cat) {
      navigate("/");
      return;
    }
    setCategory(cat);

    const flatAnswers = { ...answers };
    if (Array.isArray(flatAnswers.resistance)) {
      flatAnswers.resistance.forEach((k) => (flatAnswers[`resistance_${k}`] = true));
      delete flatAnswers.resistance;
    }

    api.post("/recommendations", { category: cat, answers: flatAnswers })
      .then((r) => {
        setResults(r.data.results || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [navigate]);

  const catInfo = CATEGORIES[category];

  return (
    <div className="min-h-screen tm-grid-bg">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3" data-testid="results-tag">
              [ Risultati · {catInfo?.title || category} ]
            </div>
            <h1 className="font-heading text-3xl md:text-5xl text-white tracking-tighter" data-testid="results-title">
              {results.length > 0 ? "Prodotti consigliati" : "Nessun prodotto trovato"}
            </h1>
            <p className="text-slate-400 mt-3" data-testid="results-count">
              {results.length > 0
                ? `Mostriamo le migliori ${results.length} soluzioni su ${total} nel catalogo.`
                : `${total} prodott${total === 1 ? "o" : "i"} analizzat${total === 1 ? "o" : "i"} nel catalogo.`}
            </p>
          </div>
          <Link to="/" className="tm-btn-outline" data-testid="new-search-btn">
            ↺ Nuova ricerca
          </Link>
        </div>

        {loading && (
          <div className="tm-card p-12 text-center" data-testid="loading">
            <div className="font-mono text-orange-500 uppercase tracking-widest text-sm animate-pulse">
              Analisi in corso...
            </div>
          </div>
        )}

        {!loading && results.length === 0 && (
          <div className="tm-card p-12 text-center" data-testid="empty-results">
            <p className="text-slate-300 mb-6 text-lg">Nessun prodotto disponibile in questa categoria.</p>
            <p className="text-slate-500 text-sm mb-6">L'amministratore deve caricare le schede tecniche.</p>
            <Link to="/" className="tm-btn-primary" data-testid="empty-back-btn">Torna alla home</Link>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-6">
            {results.map(({ product, match_score }, idx) => (
              <div
                key={product.id}
                className="tm-card tm-card-hover overflow-hidden flex flex-col"
                data-testid={`product-card-${product.id}`}
              >
                {/* IMAGE */}
                <div className="relative border-b border-slate-700 bg-slate-900 aspect-[4/3] overflow-hidden group">
                  {product.image_url ? (
                    <button
                      type="button"
                      onClick={() => setLightboxImage({ url: product.image_url, name: product.name })}
                      className="w-full h-full block cursor-zoom-in"
                      aria-label={`Ingrandisci foto ${product.name}`}
                      data-testid={`zoom-image-${product.id}`}
                    >
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        data-testid={`product-image-${product.id}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="tm-tag bg-slate-900/90 text-white border-slate-600 backdrop-blur-sm">
                          🔍 Ingrandisci
                        </span>
                      </div>
                    </button>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-6xl text-slate-700 mb-2 font-heading">◆</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-600">
                          Foto non disponibile
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-3 left-3 tm-tag bg-slate-900/80">#{String(idx + 1).padStart(2, "0")}</div>
                  <div className="absolute top-3 right-3 tm-tag bg-orange-500 text-white border-orange-600 font-bold" data-testid={`score-${product.id}`}>
                    {match_score}% MATCH
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <h2 className="font-heading text-xl text-white tracking-tighter">
                    {product.name}
                  </h2>
                  {product.brand && (
                    <div className="text-slate-400 text-sm mt-1">{product.brand}</div>
                  )}

                  {product.description && (
                    <p className="text-slate-300 text-sm mt-3 mb-4 leading-relaxed line-clamp-3">{product.description}</p>
                  )}

                  {(productForm(product) || productApplication(product)) && (
                    <div className="grid gap-3 text-sm mt-2" data-testid={`card-info-${product.id}`}>
                      {productForm(product) && (
                        <div>
                          <div className="tm-label">Forma</div>
                          <div className="text-slate-200 text-xs">{productForm(product)}</div>
                        </div>
                      )}
                      {productApplication(product) && (
                        <div>
                          <div className="tm-label">Applicazioni</div>
                          <div className="text-slate-200 text-xs line-clamp-4">{productApplication(product)}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-auto pt-6 space-y-2">
                    <button
                      onClick={() => setRequestProduct(product)}
                      className="tm-btn-primary w-full text-sm"
                      data-testid={`request-scheda-${product.id}`}
                    >
                      📩 Richiedi scheda tecnica
                    </button>
                    <PriceButton productId={product.id} productName={product.name} className="w-full" />

                    {user?.role === "admin" && product.raw_text && (
                      <button
                        onClick={() => setExpanded(expanded === product.id ? null : product.id)}
                        className="font-mono text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400 w-full text-center py-2"
                        data-testid={`expand-${product.id}`}
                      >
                        {expanded === product.id ? "− Nascondi dettagli" : "+ Dettagli tecnici (admin)"}
                      </button>
                    )}
                  </div>

                  {expanded === product.id && product.raw_text && (
                    <div className="mt-4 pt-4 border-t border-slate-700 text-xs text-slate-400 font-mono whitespace-pre-wrap max-h-48 overflow-auto">
                      {product.raw_text}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {requestProduct && (
        <SchedaRequestModal
          product={requestProduct}
          onClose={() => setRequestProduct(null)}
          onSuccess={() => {
            toast.success("Richiesta inviata! Ti contatteremo via email a breve.");
            setRequestProduct(null);
          }}
        />
      )}

      {lightboxImage && (
        <ImageLightbox
          url={lightboxImage.url}
          name={lightboxImage.name}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}

function ImageLightbox({ url, name, onClose }) {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl font-light w-12 h-12 flex items-center justify-center border border-slate-700 hover:border-orange-500 transition-colors"
        aria-label="Chiudi"
        data-testid="lightbox-close"
      >
        ×
      </button>
      <div
        className="max-w-6xl max-h-[90vh] w-full flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={url}
          alt={name}
          className="max-w-full max-h-[85vh] object-contain border border-slate-800"
          data-testid="lightbox-image"
        />
        <div className="tm-tag bg-slate-900/80 text-white border-slate-700" data-testid="lightbox-name">
          {name}
        </div>
      </div>
    </div>
  );
}

function SchedaRequestModal({ product, onClose, onSuccess }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/scheda-requests", {
        client_email: email,
        client_name: name || null,
        product_id: product.id,
        message: message || null,
      });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="tm-modal fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="scheda-modal">
      <div className="tm-card p-8 w-full max-w-lg">
        <div className="tm-tag inline-block mb-4">Richiesta scheda tecnica</div>
        <h3 className="font-heading text-2xl text-white tracking-tighter mb-2">
          {product.name}
        </h3>
        <p className="text-slate-400 text-sm mb-6">
          Lasciaci la tua email: ti invieremo la scheda tecnica completa del prodotto entro 24 ore lavorative.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tm-label">Email *</label>
            <input
              type="email"
              required
              className="tm-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tua@azienda.it"
              data-testid="scheda-email"
            />
          </div>
          <div>
            <label className="tm-label">Nome / Azienda (opzionale)</label>
            <input
              type="text"
              className="tm-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mario Rossi / Rossi S.r.l."
              data-testid="scheda-name"
            />
          </div>
          <div>
            <label className="tm-label">Note aggiuntive (opzionale)</label>
            <textarea
              className="tm-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="es. quantità richiesta, tempi di consegna, altre esigenze..."
              data-testid="scheda-message"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="tm-btn-outline flex-1" data-testid="scheda-cancel">
              Annulla
            </button>
            <button type="submit" disabled={sending} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="scheda-submit">
              {sending ? "Invio..." : "Invia richiesta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
